import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Package, Shield, Globe, CheckCircle2, AlertCircle, Upload, X, Phone, Mail, FileText, Scale } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const SupplementsProteinShippingPage = () => {
  const { toast } = useToast();
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  // Smooth spring animation for floating protein container
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const smoothProgress = useSpring(scrollYProgress, springConfig);

  // Hero protein container rotation and movement
  const heroRotate = useTransform(smoothProgress, [0, 0.3], [0, 15]);
  const heroY = useTransform(smoothProgress, [0, 0.3], [0, -100]);
  const heroScale = useTransform(smoothProgress, [0, 0.15, 0.3], [1, 1.1, 0.9]);

  // Protein powder pouring effect
  const pouringHeight = useTransform(smoothProgress, [0.2, 0.6], [0, 600]);
  const pouringOpacity = useTransform(smoothProgress, [0.2, 0.35, 0.6], [0, 1, 0.3]);

  // Product Eligibility Form State
  const [eligibilityForm, setEligibilityForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    originCity: '',
    originCountry: 'Canada',
    destinationCountry: '',
    productCategory: 'Protein Powder',
    productName: '',
    quantity: '',
    approximateWeight: '',
    productDescription: '',
    additionalComments: ''
  });

  const [uploadedImages, setUploadedImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleEligibilityChange = (e) => {
    setEligibilityForm({
      ...eligibilityForm,
      [e.target.name]: e.target.value
    });
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    addFiles(files);
  };

  const addFiles = (files) => {
    const validFiles = files.filter(file => {
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const maxSize = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: `${file.name} is not a valid image type. Please upload JPG, PNG or WEBP.`,
          variant: "destructive"
        });
        return false;
      }

      if (file.size > maxSize) {
        toast({
          title: "File Too Large",
          description: `${file.name} exceeds 5MB. Please compress and try again.`,
          variant: "destructive"
        });
        return false;
      }

      return true;
    });

    if (uploadedImages.length + validFiles.length > 5) {
      toast({
        title: "Maximum Files Exceeded",
        description: "You can upload a maximum of 5 product images.",
        variant: "destructive"
      });
      return;
    }

    // Create preview URLs
    const newImages = validFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));

    setUploadedImages([...uploadedImages, ...newImages]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  };

  const removeImage = (index) => {
    const newImages = [...uploadedImages];
    URL.revokeObjectURL(newImages[index].preview);
    newImages.splice(index, 1);
    setUploadedImages(newImages);
  };

  const handleEligibilitySubmit = async (e) => {
    e.preventDefault();
    
    if (uploadedImages.length === 0) {
      toast({
        title: "Product Photos Required",
        description: "Please upload at least one photo of the product, ingredient label, or nutrition facts.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const formData = new FormData();
      
      // Append form fields
      Object.keys(eligibilityForm).forEach(key => {
        formData.append(key, eligibilityForm[key]);
      });

      // Append images
      uploadedImages.forEach((image, index) => {
        formData.append(`productImage${index}`, image.file);
      });

      formData.append('submissionType', 'supplements');

      // TODO: Replace with actual backend endpoint once secure cloud storage is implemented
      // const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/product-eligibility`, {
      //   method: 'POST',
      //   body: formData
      // });

      // For now, show success message (backend integration pending)
      toast({
        title: "⚠️ Backend Integration Pending",
        description: "Product eligibility form UI is complete. Secure cloud storage integration required before enabling live submissions.",
        variant: "destructive"
      });

      // Simulate success for demo
      setTimeout(() => {
        toast({
          title: "Submission Successful (Demo)",
          description: "Your product details would be sent to Shippzy for review once backend storage is configured.",
        });
        
        // Reset form (demo only)
        // setEligibilityForm({ ... reset values ... });
        // setUploadedImages([]);
      }, 2000);

    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.message || "An error occurred. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: Globe,
      title: "International Supplement Shipping",
      description: "Ship eligible protein powders, whey supplements, vitamins, and fitness nutrition products from Canada to India and worldwide."
    },
    {
      icon: Shield,
      title: "Product Eligibility Review",
      description: "Our team reviews product specifications, ingredients, and destination-country import regulations before confirming shipment."
    },
    {
      icon: Package,
      title: "Secure Packaging & Handling",
      description: "Supplements require proper packaging to prevent damage. We provide guidance on container sealing and protective materials."
    },
    {
      icon: CheckCircle2,
      title: "Customs Documentation Support",
      description: "Expert assistance with customs forms, declared value, ingredient declarations, and carrier-specific requirements."
    }
  ];

  const acceptedProducts = [
    "Whey protein powder (sealed containers)",
    "Plant-based protein supplements",
    "Vitamins and multivitamins",
    "Amino acids (BCAA, creatine, glutamine)",
    "Pre-workout and post-workout supplements",
    "Protein bars and fitness nutrition",
    "Omega-3 and fish oil capsules",
    "Herbal and natural supplements"
  ];

  const restrictions = [
    "Products must be in original, sealed packaging",
    "Ingredient labels must be clearly visible",
    "Certain controlled substances are prohibited",
    "Destination country may restrict specific ingredients",
    "Carrier approval required for supplement shipments",
    "Import permits may be required by customs",
    "Prescription medications require separate authorization",
    "Loose powders without sealed containers may be rejected"
  ];

  const faqs = [
    {
      question: "Can I ship protein powder from Canada to India?",
      answer: "Yes, protein powder can generally be shipped to India if it is in original sealed packaging and contains legal, non-restricted ingredients. However, every shipment requires review and carrier approval. Submit product details and photos through our eligibility check form for confirmation."
    },
    {
      question: "What information do I need to provide?",
      answer: "You must provide photos of the product container, ingredient label, nutrition facts, and any allergen warnings. We also need product brand, quantity, destination country, and approximate weight. This information helps us verify shipping eligibility and customs requirements."
    },
    {
      question: "Are all supplements accepted for international shipping?",
      answer: "No. Acceptance depends on product ingredients, destination-country regulations, carrier restrictions, and Canadian export laws. Controlled substances, prescription medications, and products with restricted ingredients may be prohibited. Submit your product for review before shipping."
    },
    {
      question: "How long does the eligibility review take?",
      answer: "Our team typically reviews product submissions within 1-2 business days. Complex cases or products requiring additional carrier confirmation may take 3-5 business days. You will be notified via email once the review is complete."
    },
    {
      question: "What are the shipping costs for supplements?",
      answer: "Shipping costs depend on product weight, dimensions, destination country, and carrier. Protein powder typically weighs between 1-5 kg per container. Contact us for an accurate quote after your product eligibility is confirmed."
    },
    {
      question: "Do I need an import permit to ship supplements?",
      answer: "Import permit requirements vary by destination country. India, for example, may require specific declarations or permits for certain supplement categories. Shippzy will inform you of any additional requirements during the eligibility review process."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Ship Protein Powder & Supplements Internationally From Canada | Shippzy</title>
        <meta name="description" content="Ship protein powder, whey supplements, vitamins, and fitness nutrition products from Canada to India and worldwide. Eligibility review, customs support, and secure international delivery." />
        <link rel="canonical" href="https://www.shippzy.ca/shipping-supplements-protein" />
      </Helmet>

      <div ref={containerRef} className="min-h-screen bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        
        {/* Hero Section with Floating Protein Container */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-gray-900 via-red-900 to-black">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,0,0,0.1),transparent_50%)]"></div>
          </div>

          {/* Animated Floating Protein Container */}
          <motion.div
            style={{
              rotate: heroRotate,
              y: heroY,
              scale: heroScale
            }}
            className="absolute right-10 top-1/4 w-96 h-96 pointer-events-none hidden lg:block"
          >
            <motion.div
              animate={{
                y: [0, -20, 0],
                rotateY: [0, 10, 0]
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="relative w-full h-full"
            >
              {/* Placeholder for protein container - using CSS shapes for now */}
              <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-red-600 to-red-700 rounded-3xl shadow-2xl transform perspective-1000" style={{ clipPath: 'polygon(15% 0%, 85% 0%, 100% 15%, 100% 85%, 85% 100%, 15% 100%, 0% 85%, 0% 15%)' }}>
                <div className="absolute inset-4 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-white font-bold text-4xl opacity-80">
                  PROTEIN
                  <div className="text-sm font-normal mt-2 text-center">100% WHEY</div>
                </div>
              </div>
              <div className="absolute -inset-10 bg-red-500/20 blur-3xl rounded-full"></div>
            </motion.div>
          </motion.div>

          {/* Protein Powder Pouring Animation */}
          <motion.div
            style={{
              height: pouringHeight,
              opacity: pouringOpacity
            }}
            className="absolute right-1/4 top-1/3 w-24 pointer-events-none hidden lg:block"
          >
            <svg width="100%" height="100%" viewBox="0 0 100 600" preserveAspectRatio="none" className="opacity-80">
              <defs>
                <linearGradient id="powderGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor="rgba(254, 240, 200, 0.9)" />
                  <stop offset="50%" stopColor="rgba(254, 226, 160, 0.95)" />
                  <stop offset="100%" stopColor="rgba(245, 208, 140, 1)" />
                </linearGradient>
              </defs>
              <path d="M 30 0 Q 40 150, 35 300 Q 30 450, 40 600 L 60 600 Q 70 450, 65 300 Q 60 150, 70 0 Z" fill="url(#powderGradient)" />
              {/* Powder particles */}
              <circle cx="25" cy="200" r="3" fill="rgba(254, 226, 160, 0.6)" />
              <circle cx="75" cy="350" r="2" fill="rgba(254, 226, 160, 0.5)" />
              <circle cx="30" cy="450" r="2.5" fill="rgba(245, 208, 140, 0.7)" />
              <circle cx="70" cy="250" r="2" fill="rgba(254, 226, 160, 0.4)" />
            </svg>
          </motion.div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="inline-block mb-6 px-6 py-2 bg-red-600/20 border border-red-500/30 rounded-full"
              >
                <span className="text-yellow-400 font-semibold text-sm">✓ Fitness & Nutrition Shipping</span>
              </motion.div>

              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Ship Protein Powder
                <br />
                <span className="bg-gradient-to-r from-yellow-400 via-red-400 to-red-500 bg-clip-text text-transparent">
                  & Supplements Globally
                </span>
              </h1>

              <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
                International shipping for eligible protein powders, whey supplements, vitamins, and fitness nutrition products from Canada to India and worldwide.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Button 
                  className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white px-8 py-6 text-lg shadow-xl"
                  onClick={() => document.getElementById('eligibility-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Check Product Eligibility
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                  onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth' })}
                >
                  How It Works
                </Button>
              </div>

              <div className="mt-12 flex items-center justify-center gap-8 text-gray-400 text-sm">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-red-500" />
                  <span>+1 (437) 616-0758</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-red-500" />
                  <span>ship@shippzy.ca</span>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Scroll indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="text-white text-sm flex flex-col items-center gap-2"
            >
              <span className="opacity-70">Scroll to explore</span>
              <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
                <motion.div
                  animate={{ y: [0, 12, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                  className="w-1.5 h-1.5 bg-white rounded-full"
                ></motion.div>
              </div>
            </motion.div>
          </motion.div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Why Ship Supplements with Shippzy?
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Specialized handling, customs expertise, and carrier partnerships for fitness nutrition products
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="h-full hover:shadow-xl transition-shadow duration-300 border-t-4 border-red-600">
                      <CardHeader>
                        <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <CardTitle className="text-xl">{benefit.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-gray-600">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Accepted Products & Restrictions */}
        <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Accepted Products */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle2 className="w-8 h-8 text-green-600" />
                      <CardTitle className="text-2xl">Accepted Supplement Types</CardTitle>
                    </div>
                    <p className="text-gray-600">Products typically eligible for international shipping (subject to review)</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {acceptedProducts.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Restrictions */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <Card className="h-full shadow-lg border-l-4 border-yellow-500">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <AlertCircle className="w-8 h-8 text-yellow-600" />
                      <CardTitle className="text-2xl">Important Restrictions</CardTitle>
                    </div>
                    <p className="text-gray-600">Eligibility requirements and shipping limitations</p>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {restrictions.map((item, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Disclaimer */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-12 bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg"
            >
              <div className="flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Important Disclaimer</h3>
                  <p className="text-gray-700 leading-relaxed">
                    Submitting a product for review does not guarantee shipping acceptance. Eligibility depends on product specifications, ingredient composition, carrier approval, applicable Canadian export regulations, and destination-country import requirements. Certain controlled substances, prescription medications, and products with restricted ingredients may be prohibited. All shipping decisions are subject to final carrier and customs authority approval.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Product Eligibility Check Form */}
        <section id="eligibility-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Check Product Eligibility
              </h2>
              <p className="text-xl text-gray-600">
                Upload product photos and details for manual review by our shipping specialists
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <Card className="shadow-2xl border-t-4 border-red-600">
                <CardContent className="p-8">
                  <form onSubmit={handleEligibilitySubmit} className="space-y-6">
                    
                    {/* Customer Information */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                        <Input
                          name="fullName"
                          value={eligibilityForm.fullName}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="John Doe"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address *</label>
                        <Input
                          type="email"
                          name="email"
                          value={eligibilityForm.email}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="john@example.com"
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                        <Input
                          type="tel"
                          name="phone"
                          value={eligibilityForm.phone}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="+1 (123) 456-7890"
                          className="w-full"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Shipping From (City) *</label>
                        <Input
                          name="originCity"
                          value={eligibilityForm.originCity}
                          onChange={handleEligibilityChange}
                          required
                          placeholder="Toronto, Brampton, etc."
                          className="w-full"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Destination Country *</label>
                      <select
                        name="destinationCountry"
                        value={eligibilityForm.destinationCountry}
                        onChange={handleEligibilityChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      >
                        <option value="">Select destination country</option>
                        <option value="India">India</option>
                        <option value="United Kingdom">United Kingdom</option>
                        <option value="United Arab Emirates">United Arab Emirates</option>
                        <option value="United States">United States</option>
                        <option value="Australia">Australia</option>
                        <option value="Pakistan">Pakistan</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    {/* Product Information */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Product Information</h3>
                      
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Product Category *</label>
                          <select
                            name="productCategory"
                            value={eligibilityForm.productCategory}
                            onChange={handleEligibilityChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                          >
                            <option value="Protein Powder">Protein Powder (Whey, Casein, Plant-based)</option>
                            <option value="Vitamins">Vitamins & Multivitamins</option>
                            <option value="Amino Acids">Amino Acids (BCAA, Creatine, Glutamine)</option>
                            <option value="Pre-Workout">Pre-Workout Supplements</option>
                            <option value="Post-Workout">Post-Workout Recovery</option>
                            <option value="Protein Bars">Protein Bars & Snacks</option>
                            <option value="Omega-3">Omega-3 & Fish Oil</option>
                            <option value="Herbal">Herbal Supplements</option>
                            <option value="Other">Other</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Product Name/Brand *</label>
                          <Input
                            name="productName"
                            value={eligibilityForm.productName}
                            onChange={handleEligibilityChange}
                            required
                            placeholder="e.g., Optimum Nutrition Gold Standard Whey"
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6 mt-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Quantity *</label>
                          <Input
                            name="quantity"
                            value={eligibilityForm.quantity}
                            onChange={handleEligibilityChange}
                            required
                            placeholder="e.g., 2 containers"
                            className="w-full"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-2">Approximate Weight *</label>
                          <Input
                            name="approximateWeight"
                            value={eligibilityForm.approximateWeight}
                            onChange={handleEligibilityChange}
                            required
                            placeholder="e.g., 5 kg"
                            className="w-full"
                          />
                        </div>
                      </div>

                      <div className="mt-6">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Product Description *</label>
                        <textarea
                          name="productDescription"
                          value={eligibilityForm.productDescription}
                          onChange={handleEligibilityChange}
                          required
                          rows={4}
                          placeholder="Describe the product, its main ingredients, and any relevant details..."
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        ></textarea>
                      </div>
                    </div>

                    {/* Image Upload */}
                    <div className="border-t pt-6 mt-6">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">Product Photos *</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Upload photos of: product front, ingredient label, nutrition facts, sealed packaging (max 5 images, 5MB each)
                      </p>

                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                        className={`
                          border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-300
                          ${isDragging 
                            ? 'border-red-600 bg-red-50' 
                            : 'border-gray-300 hover:border-red-600 hover:bg-gray-50'
                          }
                        `}
                      >
                        <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-700 font-semibold mb-2">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-sm text-gray-500">
                          JPG, PNG or WEBP (max 5MB per file)
                        </p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </div>

                      {/* Image Previews */}
                      {uploadedImages.length > 0 && (
                        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
                          {uploadedImages.map((image, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={image.preview}
                                alt={`Product ${index + 1}`}
                                className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                              >
                                <X className="w-4 h-4" />
                              </button>
                              <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Additional Comments */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Additional Comments (Optional)</label>
                      <textarea
                        name="additionalComments"
                        value={eligibilityForm.additionalComments}
                        onChange={handleEligibilityChange}
                        rows={3}
                        placeholder="Any special requirements, questions, or additional information..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="pt-6">
                      <Button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white py-6 text-lg font-semibold shadow-xl"
                      >
                        {isSubmitting ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Submitting...
                          </span>
                        ) : (
                          'Submit for Review'
                        )}
                      </Button>

                      <p className="text-sm text-gray-600 text-center mt-4">
                        Our team will review your submission within 1-2 business days and contact you regarding shipping eligibility and applicable charges.
                      </p>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-12"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Common questions about shipping supplements internationally
              </p>
            </motion.div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="shadow-lg hover:shadow-xl transition-shadow">
                    <CardHeader>
                      <CardTitle className="text-lg text-gray-900 flex items-start gap-3">
                        <FileText className="w-5 h-5 text-red-600 flex-shrink-0 mt-1" />
                        <span>{faq.question}</span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-900 via-red-900 to-black text-white">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Ready to Ship Your Supplements?
              </h2>
              <p className="text-xl text-gray-300 mb-10">
                Submit your product for eligibility review or contact our team for assistance
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg font-semibold shadow-xl"
                  onClick={() => document.getElementById('eligibility-form').scrollIntoView({ behavior: 'smooth' })}
                >
                  Check Eligibility Now
                </Button>
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg"
                  onClick={() => window.location.href = '/contact'}
                >
                  Contact Shippzy
                </Button>
              </div>

              <div className="mt-12 flex flex-col md:flex-row items-center justify-center gap-8 text-gray-400">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-red-500" />
                  <span>+1 (437) 616-0758</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-red-500" />
                  <span>ship@shippzy.ca</span>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

      </div>
    </>
  );
};

export default SupplementsProteinShippingPage;
